import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const DOC_TYPES = [
  { value: 'passport', label: 'Passport', icon: 'BookOpen', hasBack: false },
  { value: 'national_id', label: 'National ID', icon: 'CreditCard', hasBack: true },
  { value: 'drivers_license', label: "Driver\'s License", icon: 'Car', hasBack: true },
];

const inputStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  color: '#fff',
  padding: '10px 14px',
  width: '100%',
  fontSize: '14px',
  outline: 'none',
  transition: 'border-color 0.2s',
};

const UploadZone = ({ label, hint, file, preview, onFile, uploading, progress, error, accept = 'image/*' }) => {
  const ref = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragging(false);
    const f = e?.dataTransfer?.files?.[0];
    if (f) onFile(f);
  };

  return (
    <div>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'rgba(255,255,255,0.7)', marginBottom: '6px' }}>
        {label} <span style={{ color: '#f87171' }}>*</span>
      </label>
      {hint && <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginBottom: '8px' }}>{hint}</p>}
      {preview ? (
        <div
          className="rounded-xl overflow-hidden relative"
          style={{ border: '1px solid rgba(20,184,166,0.3)', background: 'rgba(20,184,166,0.05)' }}
        >
          <img src={preview} alt={`${label} document preview`} className="w-full object-cover" style={{ maxHeight: '180px', objectFit: 'contain' }} />
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center gap-2">
              <Icon name="CheckCircle" size={14} color="#14B8A6" />
              <span style={{ fontSize: '12px', color: '#14B8A6' }}>Uploaded</span>
            </div>
            <button
              onClick={() => onFile(null)}
              style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e?.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => ref?.current?.click()}
          className="rounded-xl flex flex-col items-center justify-center gap-3 cursor-pointer transition-all"
          style={{
            border: `2px dashed ${dragging ? '#14B8A6' : error ? '#f87171' : 'rgba(255,255,255,0.12)'}`,
            background: dragging ? 'rgba(20,184,166,0.06)' : 'rgba(255,255,255,0.02)',
            padding: '28px 16px',
            minHeight: '130px',
          }}
        >
          {uploading ? (
            <>
              <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#14B8A6', borderTopColor: 'transparent' }} />
              <div style={{ width: '100%', maxWidth: '160px' }}>
                <div style={{ height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg,#0d9488,#1d4ed8)', transition: 'width 0.3s' }} />
                </div>
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', marginTop: '4px' }}>{progress}%</p>
              </div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(20,184,166,0.1)' }}>
                <Icon name="Upload" size={18} color="#14B8A6" />
              </div>
              <div className="text-center">
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontWeight: '500' }}>Drop file or click to upload</p>
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>JPG, PNG, PDF · Max 10MB</p>
              </div>
              <div className="flex items-center gap-1" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
                <Icon name="Camera" size={12} />
                <span>Mobile: use camera capture</span>
              </div>
            </>
          )}
          <input ref={ref} type="file" accept={accept} capture="environment" onChange={(e) => onFile(e?.target?.files?.[0])} className="hidden" />
        </div>
      )}
      {error && <p style={{ fontSize: '12px', color: '#f87171', marginTop: '4px' }}>{error}</p>}
    </div>
  );
};

const KYCVerificationStep = ({ data, onChange, errors, userId }) => {
  const [previews, setPreviews] = useState({ front: null, back: null, selfie: null });
  const [uploading, setUploading] = useState({ front: false, back: false, selfie: false });
  const [progress, setProgress] = useState({ front: 0, back: 0, selfie: 0 });

  const selectedDoc = DOC_TYPES?.find((d) => d?.value === data?.documentType);

  const uploadFile = async (file, field) => {
    if (!file) {
      onChange({ ...data, [`${field}Url`]: '' });
      setPreviews((p) => ({ ...p, [field]: null }));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setPreviews((p) => ({ ...p, [field]: reader?.result }));
    reader?.readAsDataURL(file);

    setUploading((u) => ({ ...u, [field]: true }));
    setProgress((p) => ({ ...p, [field]: 10 }));

    try {
      const ext = file?.name?.split('.')?.pop();
      const path = `${userId}/${field}-${Date.now()}.${ext}`;
      setProgress((p) => ({ ...p, [field]: 40 }));

      const { error: uploadError } = await supabase?.storage
        ?.from('card-documents')
        ?.upload(path, file, { upsert: true });

      if (uploadError) throw uploadError;
      setProgress((p) => ({ ...p, [field]: 80 }));

      const { data: signedData } = await supabase?.storage
        ?.from('card-documents')
        ?.createSignedUrl(path, 60 * 60 * 24 * 7);

      setProgress((p) => ({ ...p, [field]: 100 }));
      onChange({ ...data, [`${field}Url`]: path });
    } catch (err) {
      console.error('[KYC] upload error:', err);
      onChange({ ...data, [`${field}Url`]: '' });
      setPreviews((p) => ({ ...p, [field]: null }));
    } finally {
      setUploading((u) => ({ ...u, [field]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Identity Verification
        </h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Upload clear, unobstructed photos of your identity documents.
        </p>
      </div>
      {/* Document Type */}
      <div>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'rgba(255,255,255,0.7)', marginBottom: '10px' }}>
          Document Type <span style={{ color: '#f87171' }}>*</span>
        </label>
        <div className="grid grid-cols-3 gap-3">
          {DOC_TYPES?.map((doc) => {
            const isSelected = data?.documentType === doc?.value;
            return (
              <button
                key={doc?.value}
                type="button"
                onClick={() => onChange({ ...data, documentType: doc?.value })}
                className="flex flex-col items-center gap-2 p-3 rounded-xl transition-all"
                style={{
                  background: isSelected ? 'rgba(13,148,136,0.15)' : 'rgba(255,255,255,0.03)',
                  border: `1.5px solid ${isSelected ? '#14B8A6' : 'rgba(255,255,255,0.1)'}`,
                  cursor: 'pointer',
                }}
              >
                <Icon name={doc?.icon} size={20} color={isSelected ? '#14B8A6' : 'rgba(255,255,255,0.4)'} />
                <span style={{ fontSize: '12px', fontWeight: '500', color: isSelected ? '#14B8A6' : 'rgba(255,255,255,0.5)' }}>
                  {doc?.label}
                </span>
              </button>
            );
          })}
        </div>
        {errors?.documentType && <p style={{ fontSize: '12px', color: '#f87171', marginTop: '6px' }}>{errors?.documentType}</p>}
      </div>
      {/* Document Uploads */}
      {data?.documentType && (
        <div className="space-y-4">
          <UploadZone
            label="Front of Document"
            hint="Ensure all four corners are visible and text is legible"
            file={null}
            preview={previews?.front}
            onFile={(f) => uploadFile(f, 'front')}
            uploading={uploading?.front}
            progress={progress?.front}
            error={errors?.frontUrl}
          />
          {selectedDoc?.hasBack && (
            <UploadZone
              label="Back of Document"
              hint="Required for National ID and Driver's License"
              file={null}
              preview={previews?.back}
              onFile={(f) => uploadFile(f, 'back')}
              uploading={uploading?.back}
              progress={progress?.back}
              error={errors?.backUrl}
            />
          )}
        </div>
      )}
      {/* Selfie */}
      <div
        className="p-4 rounded-xl space-y-3"
        style={{ background: 'rgba(29,78,216,0.06)', border: '1px solid rgba(29,78,216,0.2)' }}
      >
        <div className="flex items-start gap-3">
          <Icon name="ScanFace" size={18} color="#60a5fa" className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>Selfie / Liveness Check</p>
            <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Take a clear selfie in good lighting. Look directly at the camera. No sunglasses or hats.
            </p>
          </div>
        </div>
        <UploadZone
          label="Selfie Photo"
          hint=""
          file={null}
          preview={previews?.selfie}
          onFile={(f) => uploadFile(f, 'selfie')}
          uploading={uploading?.selfie}
          progress={progress?.selfie}
          error={errors?.selfieUrl}
          accept="image/*"
        />
      </div>
      {/* Expiry Date */}
      <div>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'rgba(255,255,255,0.7)', marginBottom: '6px' }}>
          Document Expiry Date <span style={{ color: '#f87171' }}>*</span>
        </label>
        <input
          type="date"
          value={data?.documentExpiry || ''}
          onChange={(e) => onChange({ ...data, documentExpiry: e?.target?.value })}
          style={{ ...inputStyle, colorScheme: 'dark', maxWidth: '220px' }}
          onFocus={(e) => (e.target.style.borderColor = '#14B8A6')}
          onBlur={(e) => (e.target.style.borderColor = errors?.documentExpiry ? '#f87171' : 'rgba(255,255,255,0.1)')}
        />
        {errors?.documentExpiry && <p style={{ fontSize: '12px', color: '#f87171', marginTop: '4px' }}>{errors?.documentExpiry}</p>}
      </div>
    </div>
  );
};

export default KYCVerificationStep;
